import React from 'react';
import { Card, Badge } from '@tremor/react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { useActiveMarket } from '../../store/useMarketStore';

interface RentGapVisualizationProps {
  submarketEgi: number;
  metroEgi: number;
  submarketName: string;
}

/**
 * Visual comparison of metro vs submarket EGI.
 * Shows rent gap as opportunity indicator for value-add investment.
 */
export const RentGapVisualization: React.FC<RentGapVisualizationProps> = ({
  submarketEgi,
  metroEgi,
  submarketName
}) => {
  const market = useActiveMarket();
  if (!submarketEgi || !metroEgi) {
    return null;
  }

  // Calculate rent gap percentage
  const gapDollar = metroEgi - submarketEgi;
  const gapPercent = gapDollar / metroEgi;
  const gapPercentDisplay = Math.abs(gapPercent * 100);

  // Determine opportunity status
  const isGrowthOpportunity = gapPercent > 0.02; // Submarket < Metro by >2%
  const isAboveMetro = gapPercent < -0.02; // Submarket > Metro

  // Calculate bar widths (max = 100%)
  const maxEgi = Math.max(metroEgi, submarketEgi);
  const metroWidth = Math.round((metroEgi / maxEgi) * 100);
  const submarketWidth = Math.round((submarketEgi / maxEgi) * 100);

  // Styling based on opportunity
  let statusColor: 'green' | 'amber' | 'red' = 'amber';
  let StatusIcon = Minus;
  let statusLabel = 'At Parity';

  if (isGrowthOpportunity) {
    statusColor = 'green';
    StatusIcon = ArrowUpRight;
    statusLabel = 'Room to Grow';
  } else if (isAboveMetro) {
    statusColor = 'red';
    StatusIcon = ArrowDownRight;
    statusLabel = 'Above Metro';
  }

  return (
    <Card className="mt-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
            Rent Gap Analysis
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            Comparing {submarketName} rents to {market.display_name} metro average
          </p>
        </div>
        <Badge size="sm" color={statusColor} icon={StatusIcon}>
          {statusLabel}
        </Badge>
      </div>

      {/* Explanation Box */}
      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 mb-4">
        <p className="text-sm text-gray-700">
          <strong>What is Rent Gap?</strong> The difference between this submarket's average rent (EGI)
          and the {market.display_name} metro average. Submarkets with rents below metro often have room to grow
          as they catch up to regional averages.
        </p>
      </div>

      {/* Bar Comparison */}
      <div className="space-y-4">
        {/* Metro EGI Bar */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-600">
              {market.display_name} Metro Average (benchmark)
            </span>
            <span className="text-sm font-semibold font-mono">${metroEgi.toLocaleString()}/unit</span>
          </div>
          <div className="h-8 bg-gray-100 rounded-lg overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-lg transition-all duration-500 flex items-center justify-end pr-2"
              style={{ width: `${metroWidth}%` }}
            >
              <span className="text-sm text-white font-medium">Metro</span>
            </div>
          </div>
        </div>

        {/* Submarket EGI Bar */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-600">
              {submarketName} (this property)
            </span>
            <span className="text-sm font-semibold font-mono">${submarketEgi.toLocaleString()}/unit</span>
          </div>
          <div className="h-8 bg-gray-100 rounded-lg overflow-hidden">
            <div
              className={`h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-2 ${
                isGrowthOpportunity
                  ? 'bg-green-500'
                  : isAboveMetro
                  ? 'bg-red-400'
                  : 'bg-amber-500'
              }`}
              style={{ width: `${submarketWidth}%` }}
            >
              <span className="text-sm text-white font-medium">Submarket</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gap Indicator */}
      <div className={`mt-4 p-3 rounded-lg border ${
        isGrowthOpportunity
          ? 'bg-green-50 border-green-200'
          : isAboveMetro
          ? 'bg-red-50 border-red-200'
          : 'bg-amber-50 border-amber-200'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-semibold ${
              isGrowthOpportunity ? 'text-green-700' : isAboveMetro ? 'text-red-700' : 'text-amber-700'
            }`}>
              {gapPercentDisplay.toFixed(1)}% {isGrowthOpportunity ? 'Below' : isAboveMetro ? 'Above' : 'Near'} Metro
            </p>
            <p className={`text-sm mt-1 ${
              isGrowthOpportunity ? 'text-green-600' : isAboveMetro ? 'text-red-600' : 'text-amber-600'
            }`}>
              {isGrowthOpportunity
                ? 'Submarket rents are below metro - potential for rent growth as area catches up'
                : isAboveMetro
                ? 'Submarket rents exceed metro - limited upside from rent convergence'
                : 'Submarket rents track metro average - typical rent growth expected'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Monthly Difference</p>
            <p className={`text-lg font-bold font-mono ${
              isGrowthOpportunity ? 'text-green-600' : isAboveMetro ? 'text-red-600' : 'text-amber-600'
            }`}>
              {gapDollar >= 0 ? '+' : ''}${Math.abs(gapDollar).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Investment Implications */}
      <div className={`mt-3 p-3 rounded-lg text-sm ${
        isGrowthOpportunity
          ? 'bg-green-100 text-green-800'
          : isAboveMetro
          ? 'bg-red-50 text-red-700'
          : 'bg-amber-50 text-amber-700'
      }`}>
        <strong>Investment Implication:</strong>{' '}
        {isGrowthOpportunity
          ? `This submarket is ${gapPercentDisplay.toFixed(0)}% below metro rent. Properties here may see above-average rent growth as the area develops and rents converge toward the metro average.`
          : isAboveMetro
          ? 'This submarket already commands above-metro rents. Future rent growth may be more limited as it already leads the region.'
          : `This submarket is at parity with metro rents. Expect typical market rent growth tracking the broader ${market.display_name} metro.`}
      </div>
    </Card>
  );
};

export default RentGapVisualization;
